/**
 * onboardingSteps — pure helpers for interpreting the Xano onboarding
 * status payload. Extracted from `guards.jsx` so `AuthContext` can also
 * consume them (guards.jsx imports AuthContext, so importing the other
 * way would be a cycle).
 */

export const ONBOARDING_STEPS = ["basics", "photo", "interests", "people"];
export const stepPath = (s) => `/onboarding/${s}`;

export function getNextOnboardingStep(status) {
  if (!status) return "basics";
  if (status.completed === true || status.is_complete === true || status.onboarding_complete === true)
    return null;

  // Handle the enum-based `onboarding_status` field returned by
  // /auth/apple/web + /auth/google/web (per OpenAPI spec).
  //   "pending_email_verification" → needsEmailVerification handles it;
  //     return null here so we don't dump the user into onboarding.
  //   "pending_profile"             → start onboarding at "basics".
  //   "completed"                   → done, home.
  if (status.onboarding_status === "completed") return null;
  if (status.onboarding_status === "pending_email_verification") return null;
  if (status.onboarding_status === "pending_profile") return "basics";

  // Unverified email = must go to /verify (handled elsewhere).  Return null
  // here so the app-shell guard doesn't redirect into onboarding.
  if (status.email_verified === false) return null;

  const step = status.next_step || status.current_step || status.step;
  if (typeof step === "string") {
    if (step === "complete" || step === "done") return null;
    if (step === "verify_email") return null;
    if (step.includes("basic") || step.includes("profile_basics")) return "basics";
    if (step.includes("photo")) return "photo";
    if (step.includes("interest")) return "interests";
    if (step.includes("suggest") || step.includes("people") || step.includes("follow")) return "people";
  }

  const basicsDone = status.profile_basics_completed || status.basics_completed || !!status.username;
  const photoDone = status.profile_photo_completed || status.photo_completed || !!status.profile_photo_url;
  const interestsDone = status.interests_completed || (Array.isArray(status.interests) && status.interests.length > 0);
  const peopleDone = status.follows_completed || status.suggested_completed;

  if (!basicsDone) return "basics";
  if (!photoDone) return "photo";
  if (!interestsDone) return "interests";
  if (!peopleDone) return "people";
  return null;
}

export function needsEmailVerification(status) {
  if (!status) return false;
  if (status.onboarding_status === "pending_email_verification") return true;
  if (status.email_verified === false) return true;
  const step = status.next_step || status.current_step || status.step;
  return step === "verify_email";
}
